import { api } from './client';

/**
 * Тексты уведомлений в базе, с версиями и историей (D45, договор
 * `docs/CONTRACTS/notification-templates.md`). Ручки notification-service,
 * только роль `admin`: текст уходит полутора тысячам человек разом.
 *
 * ⚠️ КЛЮЧ СТРОКИ ПЕРЕДАЁТСЯ ПАРАМЕТРАМИ ЗАПРОСА, А НЕ В ПУТИ. Так устроен
 * сервер, и это удобно: пути здесь — голые литералы, сторож
 * `adminRoutesInGateway.test.ts` видит их и сверяет с картой шлюза и с методами
 * в Go. Методы обязаны совпадать: правка и галочка — PUT, предпросмотр — POST.
 *
 * ⚠️ ОТКАЗЫ РАЗЛИЧАЮТСЯ ТОЛЬКО ПО `code`. Текст `message` — для человека и может
 * меняться. Исключение — 403 от общей охраны ролей: он приходит НЕ в общем
 * конверте, а как `{"error": "access denied: …"}`, и `code` у него нет вовсе.
 */

/** `template` — текст события; `email_texts` — строки письма (принадлежат макету). */
export type TemplateKind = 'template' | 'email_texts';

/** Язык у СЕРВЕРА: ru | en | kz. В локалях казахский — `kk`, см. lib/notificationTemplates.ts. */
export type TemplateLang = 'ru' | 'en' | 'kz';

export type TemplateRowKey = {
  kind: TemplateKind;
  key: string;
  variant: string;
  lang: TemplateLang;
};

export type LangState = {
  lang: TemplateLang;
  /** `false` — строки нет и текст идёт из кода. В норме такого не бывает. */
  in_db: boolean;
  published: boolean;
  version?: number;
  updated_by?: string;
  updated_at?: string;
};

export type TemplateListRow = {
  kind: TemplateKind;
  key: string;
  variant?: string;
  email_layout?: string;
  /** Куда уходит событие. Справка: каналов в ручках нет, менять нельзя. */
  channels?: string[];
  langs: LangState[];
} & EventSwitchFields;

/**
 * Выключатель события целиком — все языки и варианты разом (§6а договора, 13.09.2026).
 * У всех вариантов события поля одинаковые; у строк письма — `false`, `false`, `0`.
 * Нет полей — сервер старый, выключателя нет.
 */
export type EventSwitchFields = {
  disabled?: boolean;
  /** `false` — критичное событие: сервер выключить не даст (`422 template_critical`). */
  can_disable?: boolean;
  /** Версия выключателя для `PUT …/disabled`; `0` — строки в базе нет, действует умолчание кода. */
  switch_version?: number;
};

export type EventSwitchReply = {
  event: string;
  disabled: boolean;
  can_disable: boolean;
  switch_version: number;
  updated_by?: string;
  updated_at?: string;
};

export type TemplateVersion = {
  id: string;
  title?: string;
  body?: string;
  push_title?: string;
  push_body?: string;
  texts?: Record<string, string>;
  email_layout?: string;
  published: boolean;
  version: number;
  archived_at?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
};

export type TemplateFields = {
  title?: string;
  body?: string;
  push_title?: string;
  push_body?: string;
};

export type TemplateItem = {
  kind: TemplateKind;
  key: string;
  variant?: string;
  lang: TemplateLang;
  email_layout?: string;
  channels?: string[];
  /** Действующая версия из базы; `null`, если строки нет. */
  current: TemplateVersion | null;
  /** Текст из кода на этом языке — его берёт кнопка «умолчание в поля». */
  default: TemplateFields;
  default_texts?: Record<string, string>;
  /** Какие `{{…}}` обязаны остаться в каждом поле. */
  placeholders: Record<string, string[]>;
  /** Поля, которые обязаны остаться пустыми (у письма нет пуша). */
  empty_fields?: string[];
  /**
   * Правится ли письмо этого события. У 25 шаблонов письмо — кусок вёрстки в
   * коде, и правка текста ленты ЕГО НЕ МЕНЯЕТ. Сказать об этом админу прямо в
   * интерфейсе обязательно, иначе он будет думать, что поправил письмо.
   */
  email_editable: boolean;
} & EventSwitchFields;

export type TemplatePreview = {
  title: string;
  body: string;
  push_title?: string;
  push_body?: string;
  email_html?: string;
  /** Письмо собрано из вёрстки в коде — правка текста его не изменит. */
  email_from_code: boolean;
};

export type TemplateSaveBody = TemplateRowKey &
  TemplateFields & {
    texts?: Record<string, string>;
    /** Версия, которую правили. Не совпала с текущей — 409 `template_stale`. */
    version?: number;
  };

const params = (k: TemplateRowKey) => ({ kind: k.kind, key: k.key, variant: k.variant, lang: k.lang });

/**
 * ⚠️ ПУТИ ВЫПИСАНЫ ЦЕЛИКОМ, А НЕ СОБРАНЫ ИЗ ОБЩЕЙ ПЕРЕМЕННОЙ. Сперва здесь стоял
 * `const BASE` и шаблонные строки — сторож `adminRoutesInGateway.test.ts`
 * покраснел сразу: собранный из переменной путь он проверить не может и требует
 * вписать файл в список исключений. Исключение здесь было бы вредным: именно эти
 * пути и стоит сверять с картой шлюза, потому что строка в ней одна и новая.
 */
export const notificationTemplatesApi = {
  list: () =>
    api.get<{ items: TemplateListRow[]; total: number }>('/admin/notification-templates').then((r) => r.data),

  item: (k: TemplateRowKey) =>
    api
      .get<TemplateItem>('/admin/notification-templates/item', { params: params(k) })
      .then((r) => r.data),

  save: (body: TemplateSaveBody) =>
    api.put<TemplateVersion>('/admin/notification-templates/item', body).then((r) => r.data),

  /**
   * Галочка «опубликовать» — отдельная ручка и тоже с `version`: «когда включили
   * казахский» — такой же вопрос к истории, как «когда поправили текст».
   */
  setPublished: (body: TemplateRowKey & { published: boolean; version: number }) =>
    api.put<TemplateVersion>('/admin/notification-templates/published', body).then((r) => r.data),

  history: (k: TemplateRowKey) =>
    api
      .get<{ versions: TemplateVersion[]; total: number }>('/admin/notification-templates/history', {
        params: params(k),
      })
      .then((r) => r.data),

  /** Черновик накладывается поверх сохранённого; в базу НИЧЕГО не пишется. */
  preview: (body: TemplateSaveBody) =>
    api.post<TemplatePreview>('/admin/notification-templates/preview', body).then((r) => r.data),

  /**
   * Выключить или включить событие целиком (§6а): все языки и варианты разом.
   * `version` — `switch_version`; не совпала — 409 `template_stale`. Критичное — 422 `template_critical`.
   */
  setDisabled: (body: { key: string; disabled: boolean; version: number }) =>
    api.put<EventSwitchReply>('/admin/notification-templates/disabled', body).then((r) => r.data),
};
